/**
 * Theming for the embedded Swagger UI.
 *
 * Swagger UI is served by the Go backend at /swagger/index.html and embedded
 * in an iframe, so app-level CSS cannot reach it — a stylesheet in this
 * document simply does not apply to that document. It ships a light theme and
 * rendered as a bright white slab inside the dark app.
 *
 * Two consequences shape the approach:
 *
 *  1. The overrides are INJECTED INTO THE IFRAME DOCUMENT. That is also what
 *     scopes them: they live in a document that contains nothing but Swagger
 *     UI, so there is no way for them to leak into the rest of the app.
 *  2. The iframe does not inherit `data-theme` or the token definitions, so
 *     the resolved token VALUES are read from the host document and written
 *     in as literals. Re-running the injection on a theme flip is what keeps
 *     both themes working without hardcoding either one.
 */

/** Tokens Swagger UI needs, read from the host document at injection time. */
const TOKENS = [
  '--bg', '--bg-raised', '--bg-sunken', '--surface-solid',
  '--text', '--text-secondary', '--text-faint',
  '--border', '--border-soft', '--border-strong',
  '--glass-1', '--glass-2', '--glass-3',
  '--inflow', '--inflow-text', '--outflow-text',
  '--warn', '--warn-text', '--danger', '--danger-text',
  '--info', '--info-text', '--accent', '--accent-text', '--on-accent',
  '--radius-sm', '--radius-md',
  '--font-sans', '--font-mono',
] as const;

/**
 * Snapshot the app's resolved tokens.
 *
 * getComputedStyle resolves the cascade, so whichever of the light/dark blocks
 * won is what we read — no need to reimplement the theme logic here.
 */
export function readTokens(doc: Document = document): Record<string, string> {
  const computed = doc.defaultView?.getComputedStyle(doc.documentElement);
  const out: Record<string, string> = {};
  for (const name of TOKENS) {
    const value = computed?.getPropertyValue(name).trim();
    if (value) out[name] = value;
  }
  return out;
}

/** Build the stylesheet, with the host's token values baked in as literals. */
export function buildSwaggerCSS(tokens: Record<string, string>): string {
  const t = (name: string, fallback: string) => tokens[name] || fallback;

  // Fallbacks are the dark palette, which is the app's default theme; they
  // only ever apply if a token is missing entirely.
  const bg = t('--bg', '#0a0f16');
  const raised = t('--bg-raised', '#111823');
  const sunken = t('--bg-sunken', '#070b10');
  const text = t('--text', '#f1f5f9');
  const dim = t('--text-secondary', '#9aa4b2');
  const faint = t('--text-faint', '#7d8797');
  const border = t('--border', 'rgba(255,255,255,0.10)');
  const borderSoft = t('--border-soft', 'rgba(255,255,255,0.06)');
  const glass1 = t('--glass-1', 'rgba(255,255,255,0.035)');
  const glass2 = t('--glass-2', 'rgba(255,255,255,0.055)');
  const inflow = t('--inflow', '#2dd4a7');
  const warn = t('--warn', '#ffbd66');
  const danger = t('--danger', '#ff6b86');
  const info = t('--info', '#8b7cff');
  const accent = t('--accent', '#2dd4a7');
  const onAccent = t('--on-accent', '#062018');
  const radius = t('--radius-sm', '6px');
  const sans = t('--font-sans', 'Inter, system-ui, sans-serif');
  const mono = t('--font-mono', 'ui-monospace, monospace');

  return `
/* Injected by FolioWorth. Scoped by living inside the Swagger iframe. */
html, body { background: ${bg} !important; }
body, .swagger-ui { font-family: ${sans} !important; color: ${text} !important; }

/* Swagger sets explicit dark-on-light colours all over; sweep them to tokens
   rather than chasing each selector, then re-specialise below. */
.swagger-ui, .swagger-ui .info, .swagger-ui .info li, .swagger-ui .info p,
.swagger-ui .info table, .swagger-ui .info h1, .swagger-ui .info h2,
.swagger-ui .info h3, .swagger-ui .info h4, .swagger-ui .info h5,
.swagger-ui .scheme-container, .swagger-ui .opblock-tag,
.swagger-ui label, .swagger-ui table thead tr td, .swagger-ui table thead tr th,
.swagger-ui .parameter__name, .swagger-ui .parameter__type,
.swagger-ui .response-col_status, .swagger-ui .responses-inner h4,
.swagger-ui .responses-inner h5, .swagger-ui .opblock .opblock-section-header h4,
.swagger-ui .opblock .opblock-summary-description,
.swagger-ui .model-title, .swagger-ui .model, .swagger-ui .tab li,
.swagger-ui .btn, .swagger-ui select, .swagger-ui .dialog-ux .modal-ux-header h3,
.swagger-ui .dialog-ux .modal-ux-content h4,
.swagger-ui .dialog-ux .modal-ux-content p { color: ${text} !important; }

.swagger-ui .info .title small pre { background: ${glass2} !important; }
.swagger-ui .info .base-url, .swagger-ui .info__license,
.swagger-ui .parameter__in, .swagger-ui .parameter__extension,
.swagger-ui .opblock-description-wrapper p,
.swagger-ui .opblock-external-docs-wrapper p,
.swagger-ui .opblock-title_normal p,
.swagger-ui .response-col_description__inner p,
.swagger-ui .markdown p, .swagger-ui .renderedMarkdown p { color: ${dim} !important; }

.swagger-ui .topbar { background: ${sunken} !important;
  border-bottom: 1px solid ${border} !important; }

/* Panels: hairline rules and a barely-there fill, matching the app. */
.swagger-ui .scheme-container { background: ${raised} !important;
  box-shadow: none !important; border-bottom: 1px solid ${borderSoft} !important; }
.swagger-ui .opblock-tag { border-bottom: 1px solid ${borderSoft} !important; }
.swagger-ui section.models { background: ${glass1} !important;
  border: 1px solid ${borderSoft} !important; border-radius: ${radius} !important; }
.swagger-ui section.models .model-container { background: ${glass1} !important; }
.swagger-ui .model-box { background: ${glass2} !important; }

.swagger-ui .opblock { background: ${glass1} !important;
  border: 1px solid ${borderSoft} !important; box-shadow: none !important;
  border-radius: ${radius} !important; }
.swagger-ui .opblock .opblock-section-header { background: ${glass2} !important;
  box-shadow: none !important; border-bottom: 1px solid ${borderSoft} !important; }

/* Method colours carry meaning, so they are mapped to the app's semantic
   tokens rather than flattened: read stays neutral, writes warn, delete is
   danger. Tinted fills instead of Swagger's solid pastel blocks. */
.swagger-ui .opblock.opblock-get { border-color: ${info} !important;
  background: color-mix(in srgb, ${info} 8%, transparent) !important; }
.swagger-ui .opblock.opblock-get .opblock-summary-method { background: ${info} !important; }
.swagger-ui .opblock.opblock-post { border-color: ${inflow} !important;
  background: color-mix(in srgb, ${inflow} 8%, transparent) !important; }
.swagger-ui .opblock.opblock-post .opblock-summary-method { background: ${inflow} !important; }
.swagger-ui .opblock.opblock-put, .swagger-ui .opblock.opblock-patch {
  border-color: ${warn} !important;
  background: color-mix(in srgb, ${warn} 8%, transparent) !important; }
.swagger-ui .opblock.opblock-put .opblock-summary-method,
.swagger-ui .opblock.opblock-patch .opblock-summary-method { background: ${warn} !important; }
.swagger-ui .opblock.opblock-delete { border-color: ${danger} !important;
  background: color-mix(in srgb, ${danger} 8%, transparent) !important; }
.swagger-ui .opblock.opblock-delete .opblock-summary-method { background: ${danger} !important; }
.swagger-ui .opblock .opblock-summary-method { color: ${onAccent} !important;
  text-shadow: none !important; font-family: ${mono} !important; }
.swagger-ui .opblock .opblock-summary { border-color: transparent !important; }
.swagger-ui .opblock-summary-path, .swagger-ui .opblock-summary-path__deprecated {
  color: ${text} !important; font-family: ${mono} !important; }

/* Code, examples and the "try it out" surfaces. */
.swagger-ui .highlight-code > .microlight, .swagger-ui .microlight,
.swagger-ui textarea, .swagger-ui .body-param__text {
  background: ${sunken} !important; color: ${text} !important;
  font-family: ${mono} !important;
  border: 1px solid ${borderSoft} !important; border-radius: ${radius} !important; }
.swagger-ui input[type=text], .swagger-ui input[type=password],
.swagger-ui input[type=email], .swagger-ui input[type=search],
.swagger-ui input[type=file], .swagger-ui select, .swagger-ui textarea {
  background: ${glass2} !important; color: ${text} !important;
  border: 1px solid ${border} !important; border-radius: ${radius} !important; }
.swagger-ui input::placeholder, .swagger-ui textarea::placeholder { color: ${faint} !important; }

.swagger-ui table tbody tr td { border-color: ${borderSoft} !important; color: ${text} !important; }
.swagger-ui table thead tr td, .swagger-ui table thead tr th {
  border-bottom: 1px solid ${border} !important; color: ${dim} !important; }

.swagger-ui .btn { background: transparent !important; color: ${text} !important;
  border: 1px solid ${border} !important; box-shadow: none !important;
  border-radius: ${radius} !important; }
/* Accent is reserved for the one primary action. */
.swagger-ui .btn.execute { background: ${accent} !important; color: ${onAccent} !important;
  border-color: ${accent} !important; }
.swagger-ui .btn.authorize { color: ${inflow} !important; border-color: ${inflow} !important; }
.swagger-ui .btn.authorize svg { fill: ${inflow} !important; }

.swagger-ui .dialog-ux .modal-ux { background: ${raised} !important;
  border: 1px solid ${border} !important; }
.swagger-ui .dialog-ux .modal-ux-header { border-bottom: 1px solid ${borderSoft} !important; }
.swagger-ui .dialog-ux-backdrop { background: rgba(0, 0, 0, 0.6) !important; }

/* Swagger draws several glyphs as inline SVG with a hardcoded fill. */
.swagger-ui svg:not(:root) { fill: ${text} !important; }
.swagger-ui .expand-methods svg, .swagger-ui .expand-operation svg { fill: ${faint} !important; }

.swagger-ui .tab li { color: ${dim} !important; }
.swagger-ui .tab li.active { color: ${text} !important; }
.swagger-ui .loading-container .loading::after { color: ${dim} !important; }
.swagger-ui .errors-wrapper { background: color-mix(in srgb, ${danger} 10%, transparent) !important;
  border: 1px solid ${danger} !important; }
.swagger-ui .errors-wrapper .errors h4, .swagger-ui .errors-wrapper hgroup h4 {
  color: ${danger} !important; }

::-webkit-scrollbar { width: 10px; height: 10px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: ${border}; border-radius: 999px; }
`;
}

/** The id used for the injected <style>, so re-injection replaces it. */
export const SWAGGER_STYLE_ID = 'folioworth-swagger-theme';

/**
 * Apply the theme to an already-loaded Swagger document.
 *
 * Safe to call repeatedly: the style element is replaced, not appended, so a
 * theme flip does not stack stylesheets.
 *
 * Returns true when the styles were applied. False means the iframe document
 * was unreachable — in practice a cross-origin deployment, where the page
 * still works, just unstyled.
 */
export function applySwaggerTheme(frame: HTMLIFrameElement, hostDoc: Document = document): boolean {
  let doc: Document | null = null;
  try {
    doc = frame.contentDocument;
  } catch {
    return false;
  }
  if (!doc?.head) return false;

  doc.getElementById(SWAGGER_STYLE_ID)?.remove();
  const style = doc.createElement('style');
  style.id = SWAGGER_STYLE_ID;
  style.textContent = buildSwaggerCSS(readTokens(hostDoc));
  doc.head.appendChild(style);

  // Swagger's own <html> carries no theme signal, so form controls and
  // scrollbars inside would still render light. This is the one line that
  // fixes them wholesale.
  doc.documentElement.style.colorScheme = resolveScheme(hostDoc);
  return true;
}

/**
 * Which theme is actually showing in the host document.
 *
 * The app stamps data-theme on <html>, but before that effect runs the tokens
 * fall back to prefers-color-scheme — so the attribute alone is not the whole
 * answer, and assuming dark would flash a dark iframe inside a light app.
 */
export function resolveScheme(hostDoc: Document = document): 'light' | 'dark' {
  const attr = hostDoc.documentElement.getAttribute('data-theme');
  if (attr === 'light' || attr === 'dark') return attr;
  // Guarded: matchMedia is absent in some non-browser DOM implementations,
  // and dark is the app's declared default when nothing says otherwise.
  const view = hostDoc.defaultView;
  if (typeof view?.matchMedia !== 'function') return 'dark';
  return view.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}
